<template>
  <s-modal
    v-model="showModal"
    class="secret-question-modal"
    :title="$t('my-data.secret-question-modal.change-secret-question')"
    :ok-title="$t('general.save')"
    :ok-disabled="invalidData"
    :is-loading="loading"
    @ok="save"
    @show="show"
    @cancel="closeModal"
  >
    <div class="secret-question-modal__tips text-green">
      {{ $t('my-data.secret-question-modal.tips') }}
    </div>
    <div class="secret-question-modal__body">
      <div class="secret-question-modal__field-group" role="group">
        <label class="secret-question-modal__field-label" for="password">
          {{ $t('my-data.secret-question-modal.password') }}
        </label>
        <password-eye
          v-model="password"
          id="password"
          maxlength="20"
          type="password"
          :state="passwordState"
        />
        <b-tooltip
          :show="showPasswordTooltip()"
          target="password"
          triggers=""
          placement="right"
          boundary="window"
          :fallback-placement="['right']"
        >
          {{
            $t('messages.required.message', {
              field: $t('my-data.secret-question-modal.password'),
            })
          }}
        </b-tooltip>
      </div>
      <div class="secret-question-modal__field-group" role="group">
        <label class="secret-question-modal__field-label" for="question">
          {{ $t('my-data.secret-question-modal.question') }}
        </label>
        <b-input
          v-model="question"
          class="text-uppercase"
          id="question"
          maxlength="150"
          :state="questionState"
        />
        <b-tooltip
          :show="showQuestionTooltip()"
          target="question"
          triggers=""
          placement="right"
          boundary="window"
          :fallback-placement="['right']"
        >
          {{ questionTooltipMessage() }}
        </b-tooltip>
      </div>
      <div class="secret-question-modal__field-group" role="group">
        <label class="secret-question-modal__field-label" for="answer">
          {{ $t('my-data.secret-question-modal.answer') }}
        </label>
        <b-input
          v-model="answer"
          class="text-uppercase"
          id="answer"
          maxlength="100"
          :state="answerState"
        />
        <b-tooltip
          :show="showAnswerTooltip()"
          target="answer"
          triggers=""
          placement="right"
          boundary="window"
          :fallback-placement="['right']"
        >
          {{ answerTooltipMessage() }}
        </b-tooltip>
      </div>
      <div class="secret-question-modal__captcha" role="group">
        <captcha v-if="showCaptcha" ref="captcha" />
      </div>
    </div>
  </s-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import Captcha from '@/components/captcha/Captcha';
import commonCaptchaService from '@/services/security/common';
import UserService from '@/domain/services/user';
import PasswordEye from '@/components/inputs/PasswordEye';

export default {
  constants: {
    QUESTION_MIN_LENGTH: 10,
    QUESTION_MAX_LENGTH: 150,
    ANSWER_MIN_LENGTH: 3,
    ANSWER_MAX_LENGTH: 100,
  },
  props: {
    value: {
      type: Boolean,
    },
  },
  components: {
    Captcha,
    PasswordEye,
  },
  data() {
    return {
      password: null,
      question: null,
      answer: null,
      submitAttempted: false,
      activeInvalidField: null,
      showCaptcha: false,
      loading: false,
    };
  },
  validations: {
    password: {
      required,
    },
    question: {
      required,
    },
    answer: {
      required,
    },
  },
  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    invalidData: {
      cache: false,
      get() {
        return this.$v.$invalid;
      },
    },
    passwordState() {
      return this.$bsStateValidator(this.submitAttempted && !this.$v.password.required);
    },
    questionState() {
      return this.$bsStateValidator(
        this.submitAttempted
          && this.activeInvalidField === 'question'
          && this.isQuestionLengthInvalid(),
      );
    },
    answerState() {
      return this.$bsStateValidator(
        this.submitAttempted
          && this.activeInvalidField === 'answer'
          && this.isAnswerLengthInvalid(),
      );
    },
  },
  methods: {
    isQuestionRequiredInvalid() {
      return !this.$v.question.required;
    },
    isAnswerRequiredInvalid() {
      return !this.$v.answer.required;
    },
    isQuestionLengthInvalid() {
      const questionLength = (this.question || '').length;

      return questionLength < this.$options.constants.QUESTION_MIN_LENGTH
        || questionLength > this.$options.constants.QUESTION_MAX_LENGTH;
    },
    isAnswerLengthInvalid() {
      const answerLength = (this.answer || '').length;

      return answerLength < this.$options.constants.ANSWER_MIN_LENGTH
        || answerLength > this.$options.constants.ANSWER_MAX_LENGTH;
    },
    showLengthRangeAlert(minLength, maxLength) {
      this.$pushAlert({
        code: 'MG001',
        messageParams: {
          0: String(minLength),
          1: String(maxLength),
        },
      });
    },
    clear() {
      this.password = null;
      this.question = null;
      this.answer = null;
      this.submitAttempted = false;
      this.activeInvalidField = null;
      this.showCaptcha = false;
      this.$v.$reset();
    },
    show() {
      this.clear();
    },
    validateCaptcha() {
      const { captcha } = this.$refs;
      captcha
        .validateCaptcha()
        .then(() => {
          this.doServiceSave();
        })
        .catch(() => {
          captcha.getCaptcha();
        });
    },
    save() {
      this.submitAttempted = true;
      this.activeInvalidField = null;

      if (this.isQuestionRequiredInvalid()) {
        this.activeInvalidField = 'question';
        return;
      }

      if (this.isQuestionLengthInvalid()) {
        this.activeInvalidField = 'question';
        this.showLengthRangeAlert(
          this.$options.constants.QUESTION_MIN_LENGTH,
          this.$options.constants.QUESTION_MAX_LENGTH,
        );
        return;
      }

      if (this.isAnswerRequiredInvalid()) {
        this.activeInvalidField = 'answer';
        return;
      }

      if (this.isAnswerLengthInvalid()) {
        this.activeInvalidField = 'answer';
        this.showLengthRangeAlert(
          this.$options.constants.ANSWER_MIN_LENGTH,
          this.$options.constants.ANSWER_MAX_LENGTH,
        );
        return;
      }

      if (this.showCaptcha) {
        this.validateCaptcha();
        return;
      }
      this.doServiceSave();
    },
    showCaptchaHandler() {
      if (this.showCaptcha) {
        this.$refs.captcha.getCaptcha();
      }
      this.showCaptcha = true;
    },
    handleServiceSaveError(error) {
      const errorData = commonCaptchaService.extractErrorInformation(error);

      if (
        commonCaptchaService.shouldShowCaptcha(
          errorData.blockedUser,
          errorData.wrongAttempts,
        )
      ) {
        this.showCaptchaHandler();
        return;
      }
      this.showCaptcha = false;
    },
    doServiceSave() {
      this.loading = true;

      UserService.changeSecretQuestion(
        this.password,
        this.question.toUpperCase(),
        this.answer.toUpperCase(),
      )
        .then(() => {
          this.showModal = false;
          this.$pushAlert({
            code: 'MS001',
          });
        })
        .catch((error) => {
          this.handleServiceSaveError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    closeModal() {
      this.showModal = false;
    },
    showPasswordTooltip() {
      return this.submitAttempted && !this.$v.password.required;
    },
    showQuestionTooltip() {
      return this.submitAttempted
        && this.activeInvalidField === 'question'
        && (this.isQuestionRequiredInvalid() || this.isQuestionLengthInvalid());
    },
    showAnswerTooltip() {
      return this.submitAttempted
        && this.activeInvalidField === 'answer'
        && (this.isAnswerRequiredInvalid() || this.isAnswerLengthInvalid());
    },
    questionTooltipMessage() {
      if (this.isQuestionRequiredInvalid()) {
        return this.$t('messages.required.message', {
          field: this.$t('my-data.secret-question-modal.question'),
        });
      }

      return this.$t('messages.MG001.message', {
        0: String(this.$options.constants.QUESTION_MIN_LENGTH),
        1: String(this.$options.constants.QUESTION_MAX_LENGTH),
      });
    },
    answerTooltipMessage() {
      if (this.isAnswerRequiredInvalid()) {
        return this.$t('messages.required.message', {
          field: this.$t('my-data.secret-question-modal.answer'),
        });
      }

      return this.$t('messages.MG001.message', {
        0: String(this.$options.constants.ANSWER_MIN_LENGTH),
        1: String(this.$options.constants.ANSWER_MAX_LENGTH),
      });
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.secret-question-modal
  &__tips
    font-style: italic
  &__field-group
    margin-bottom: 20px
    &:first-child
      margin-top: 20px
  &__field-label
    font-weight: 600
  &__body
    display: flex
    flex-direction: column
    align-items: center
    > *
      width: 70%
  &__captcha
    display: flex
    flex-direction: column
    align-items: center
    width: 100%
    margin-bottom: 20px
  +media-min-sm()
  +media-min-md()
  +media-min-lg()
</style>
